import { Modal, Stack, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { ResponseModal } from '@hotjar/feedback/components/common/ResponseModal';
import type { ResponseModalProps } from '@hotjar/feedback/components/common/ResponseModal';
import { ResponseModalContent } from '@hotjar/feedback/components/IncomingFeedback/Responses/ResponseModalContent';
import { RecordingLink } from '@hotjar/feedback/components/common/RecordingLink';
import type { ResponseModalContentProps } from '@hotjar/feedback/components/IncomingFeedback/Responses/ResponseModalContent';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { useTranslation } from '@hotjar/translations';

export type FeedbackResponseModalProps = ResponseModalProps &
  Omit<ResponseModalContentProps, 'canDelete'> & { coveragePercentage: number };

const StyledResponseModal = styled(ResponseModal)`
  width: 1100px;
`;

const ModalBody = styled(Modal.Body)`
  padding-bottom: 0;
`;

const ModalFooter = styled(Modal.Footer)`
  padding-top: ${tokens.spacer.x2};
`;

export function FeedbackResponseModal({
  siteId,
  feedbackId,
  response,
  emotionStyle,
  commentsProps,
  coveragePercentage,
  onDelete,
  onFeedbackChange,
  ...modalProps
}: FeedbackResponseModalProps): JSX.Element | null {
  const { hasPermission } = useAuthorization();
  const { t } = useTranslation('feedback', { keyPrefix: 'feedback-response-modal' });
  if (!modalProps.isOpen) return null;

  const hasResponseDeletePermission = hasPermission(
    'site',
    `${siteId}`,
    'feedbacks-responses-delete',
  );

  return (
    <StyledResponseModal {...modalProps} largeTopOffset>
      <Modal.Header title={t('title', { responseIndex: response.index })} />
      <ModalBody>
        <ResponseModalContent
          siteId={siteId}
          feedbackId={feedbackId}
          response={response}
          emotionStyle={emotionStyle}
          commentsProps={commentsProps}
          canDelete={hasResponseDeletePermission}
          onDelete={onDelete}
          onFeedbackChange={onFeedbackChange}
        />
      </ModalBody>
      <ModalFooter>
        <Stack spacing="x1">
          <RecordingLink
            url={response.recording_url}
            opener="incoming-feedback-response"
            type="button"
            trackTriggerView="feedback.responses"
            trackUiPosition="incoming_feedback_response_details_sidebar_play_button"
            coveragePercentage={coveragePercentage}
          />
        </Stack>
      </ModalFooter>
    </StyledResponseModal>
  );
}
